import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './InspectionPhotos.css';
import PhotoModal from '../PhotoModal';
import { INSPECTION_STATUS, INSPECTION_STATUS_VERDICT } from '../../utils/constants';
import { useAuthContext } from '../../context/AuthContext';
import Modal from '../Modal';
import PhotoPreview from '../PhotoPreviewModal/PhotoPreview';
import { createPreviewHoverHandlers } from '../../utils/previewHover';

const { APPROVED, REJECTED, PENDING_USER } = INSPECTION_STATUS;
const { PENDING } = INSPECTION_STATUS_VERDICT;

const InspectionPhotos = ({
    inspection,
    photoUrls = {},
    enableRequestNewPhotos,
    onDownloadPhotosZip,
    onDownloadPhoto,
    onSavePhotoChanges
}) => {
    const [activeTab, setActiveTab] = useState('documents');
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [showRejectionModal, setShowRejectionModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const [localPhotoChanges, setLocalPhotoChanges] = useState({});
    const [showSaveButton, setShowSaveButton] = useState(false);

    const [previewPhoto, setPreviewPhoto] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [anchorPosition, setAnchorPosition] = useState(null);
    const showPreviewTimeoutRef = useRef(null);
    const hidePreviewTimeoutRef = useRef(null);


    const { currentUser } = useAuthContext();

    const handleDownloadPhotosZip = () => {
        if (onDownloadPhotosZip && inspection.id) {
            onDownloadPhotosZip(inspection.id);
        }
    };

    const hidePreview = useCallback(() => {
        setShowPreview(false);
        setPreviewPhoto(null);
        setAnchorPosition(null);
    }, []);

    const {
        handlePhotoMouseEnter,
        handlePhotoMouseLeave,
        handlePreviewHoverChange,
    } = useMemo(
        () => createPreviewHoverHandlers({
            showTimeoutRef: showPreviewTimeoutRef,
            hideTimeoutRef: hidePreviewTimeoutRef,
            showDelayMs: 150,
            onAnchorSet: setAnchorPosition,
            onShowPreview: (photo) => {
                setPreviewPhoto(photo);
                setShowPreview(true);
            },
            onHide: hidePreview,
        }),
        [hidePreview]
    );

    useEffect(() => {
        return () => {
            if (showPreviewTimeoutRef.current) {
                clearTimeout(showPreviewTimeoutRef.current);
            }
            if (hidePreviewTimeoutRef.current) {
                clearTimeout(hidePreviewTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const hasRejectedPhotos = Object.values(localPhotoChanges).some(change => {
            const status = change.status;
            return status === REJECTED || status === 'rechazado';
        });

        if (hasRejectedPhotos && ![APPROVED, REJECTED].includes(inspection.status)) {
            setShowSaveButton(true);
        } else if (!hasRejectedPhotos && Object.keys(localPhotoChanges).length === 0) {
            setShowSaveButton(false);
        }
    }, [localPhotoChanges, inspection.status]);

    const getPhotoStatus = (photo) => {
        const localChange = localPhotoChanges[photo.id];
        if (localChange && localChange.status) {
            return localChange.status;
        }
        // Retornar el status de la foto, normalizando si es necesario
        return photo.status || PENDING;
    };

    const getPhotoWithLocalChanges = (photo) => {
        const localChange = localPhotoChanges[photo.id];
        const photoWithUrl = {
            ...photo,
            url: photoUrls[photo.id] || photo.url || '' // Usar URL del estado separado
        };
        if (localChange) {
            return { ...photoWithUrl, ...localChange };
        }
        return photoWithUrl;
    };

    const applyLocalPhotoChange = (photo, changes) => {
        setLocalPhotoChanges(prev => ({
            ...prev,
            [photo.id]: {
                ...prev[photo.id],
                ...changes
            }
        }));
    };

    const clearLocalChanges = () => {
        setLocalPhotoChanges({});
    };

    // Organize photos by category (enriqueciendo con URLs del estado separado)
    const organizePhotosByCategory = () => {
        const categories = {
            documents: [],
            exterior: [],
            interior: [],
            accessories: []
        };

        inspection.photos.forEach(photo => {
            if (photo.category) {
                if (categories[photo.category]) {
                    // Asegurar que la foto tenga la URL del estado separado
                    const photoWithUrl = {
                        ...photo,
                        url: photoUrls[photo.id] || photo.url || ''
                    };
                    categories[photo.category].push(photoWithUrl);
                }
            } else if (photo.isVideo) {
                // Si es un video sin categoría explícita, agregarlo a exterior
                const photoWithUrl = {
                    ...photo,
                    url: photoUrls[photo.id] || photo.url || '',
                    category: 'exterior'
                };
                categories.exterior.push(photoWithUrl);
            }
        });

        // Ordenar fotos exteriores: primero las fotos normales, luego el video360 al final
        if (categories.exterior.length > 0) {
            categories.exterior.sort((a, b) => {
                // Si uno es video y el otro no, el video va al final
                if (a.isVideo && !b.isVideo) return 1;
                if (!a.isVideo && b.isVideo) return -1;
                // Si ambos son del mismo tipo, mantener el orden original
                return 0;
            });
        }

        return categories;
    };

    // Helper function to chunk array into groups of 5
    const chunkArray = (array, size) => {
        const chunks = [];
        for (let i = 0; i < array.length; i += size) {
            chunks.push(array.slice(i, i + size));
        }
        return chunks;
    };

    const categories = organizePhotosByCategory();

    const getPhotoCount = (category) => {
        return categories[category] ? categories[category].length : 0;
    };

    const handlePhotoClick = (photo) => {
        setSelectedPhoto(photo);
        setOpenModal(true);
    };

    const closeModal = () => {
        setSelectedPhoto(null);
        setOpenModal(false);
    };

    const enablePhotoSection = () => {
        return (inspection.photos.length > 0);
    };

    // const enableStartButtonPhotosSection = () => {
    //     if (
    //         inspection.photos.length > 0 &&
    //         ![APPROVED, REJECTED, PENDING_USER].includes(inspection.status) &&
    //         (currentUser.role === ADMINISTRATOR || (currentUser.role === INSPECTOR && isAssignedToUser('photosInspection'))) &&
    //         (!inspection.photosInspection || [PENDING].includes(inspection.photosInspection?.status))
    //     ) {
    //         return true;
    //     }
    //     return false;
    // };

    // const handleStartButtonPhotosSection = () => {
    //     assignPhotosSectionToUser();
    //     setShowSaveButton(true);
    // };

    const handleShowRejectionModal = () => {
        setShowRejectionModal(true);
    };

    const handleRejectSelectedPhoto = (photo) => {
        const updatedPhoto = {
            ...photo,
            status: REJECTED,
            finishedBy: currentUser.name,
            finishedAt: new Date().toISOString(),
            rejectionReason: rejectionReason,
        };

        setSelectedPhoto(updatedPhoto);
        setShowRejectionModal(false);
        setRejectionReason('');

        applyLocalPhotoChange(photo, {
            status: REJECTED,
            finishedBy: currentUser.name,
            finishedAt: new Date().toISOString(),
            rejectionReason: rejectionReason
        });
    };

    const getSectionApprovalInfo = () => {
        if (inspection.photosInspection?.status === APPROVED && inspection.photosInspection?.finishedBy && inspection.photosInspection?.finishedAt) {
            return {
                approvedBy: inspection.photosInspection.finishedBy,
                approvedAt: new Date(inspection.photosInspection.finishedAt).toLocaleString('es-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                })
            };
        }
        return null;
    };

    const getRejectedPhotos = () => {
        return Object.entries(localPhotoChanges)
            .filter(([key, change]) => {
                const status = change.status;
                return status === REJECTED || status === 'rechazado';
            })
            .map(([key, change]) => {
                const originalPhoto = inspection.photos.find(p => p.id === key);
                return {
                    ...originalPhoto,
                    ...change
                };
            });
    };

    const handleSaveAllChanges = () => {
        const rejectedPhotos = getRejectedPhotos();
        if (rejectedPhotos.length > 0) {
            // Mostrar modal de confirmación de rechazos
            setShowConfirmModal(true);
        } else {
            // Guardar directamente sin rechazos
            saveChangesToDynamo();
        }
    };

    // 🔧 NUEVO: Función para guardar cambios en DynamoDB
    const saveChangesToDynamo = () => {
        const updatedPhotos = inspection.photos.map(photo => {
            const localChange = localPhotoChanges[photo.id];
            const { url, ...photoWithoutUrl } = photo;

            if (localChange) {
                const updatedPhoto = {
                    ...photoWithoutUrl,
                    ...localChange,
                    status: localChange.status || photoWithoutUrl.status || PENDING
                };
                return updatedPhoto;
            }
            return photoWithoutUrl;
        });

        const hasRejectedPhotos = updatedPhotos.some(photo => photo.status === REJECTED || photo.status === 'rechazado');

        const updatedInspection = {
            ...inspection,
            photos: updatedPhotos,
            photosInspection: {
                ...inspection.photosInspection,
                status: hasRejectedPhotos ? REJECTED : (inspection.photosInspection?.status || PENDING),
                finishedBy: hasRejectedPhotos ? currentUser.name : (inspection.photosInspection?.finishedBy || null),
                finishedAt: hasRejectedPhotos ? new Date().toISOString() : (inspection.photosInspection?.finishedAt || null)
            },
            status: hasRejectedPhotos ? PENDING_USER : inspection.status
        };

        onSavePhotoChanges(updatedInspection);

        clearLocalChanges();
        setShowSaveButton(false);
    };

    // 🔧 NUEVO: Función para confirmar rechazos y guardar
    const confirmAllRejections = async () => {
        setShowConfirmModal(false);
        saveChangesToDynamo();
    };

    const isEnabledSaveButton = () => {
        if (!inspection.photos || inspection.photos.length === 0) {
            return false;
        }

        return Object.keys(localPhotoChanges).length > 0;
    };

    const renderPhotoGrid = (photoList, categoryName) => {
        if (!photoList || photoList.length === 0) {
            return (
                <div className="inspection-photos-no-photos">
                    <p>No hay fotos disponibles para esta sección</p>
                </div>
            );
        }

        const photoRows = chunkArray(photoList, 5);

        return (
            <div className="inspection-photos-photo-section">
                {photoRows.map((row, rowIndex) => (
                    <div key={rowIndex} className="inspection-photos-photo-row">
                        {row.map((photo, photoIndex) => {
                            const photoWithChanges = getPhotoWithLocalChanges(photo);
                            const isVideo = photo.isVideo || photo.id === 'video360';
                            // Asegurar que siempre se use la URL del estado separado
                            const photoUrl = photoUrls[photo.id] || photo.url || '';

                            return (
                                <div key={photoIndex} className="inspection-photos-photo-thumbnail">
                                    <div className="inspection-photos-thumbnail-image">
                                        <div
                                            className={`inspection-photos-status-bar ${getPhotoStatus(photo)}`}
                                        >
                                            {getPhotoStatus(photo) === APPROVED && (
                                                <svg width="6" height="6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                    <path d="M20 6L9 17l-5-5" />
                                                </svg>
                                            )}
                                            {getPhotoStatus(photo) === REJECTED && (
                                                <svg width="6" height="6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                    <path d="M18 6L6 18M6 6l12 12" />
                                                </svg>
                                            )}
                                        </div>
                                        {isVideo ? (
                                            <div className="inspection-photos-video-thumbnail" onClick={() => handlePhotoClick(photoWithChanges)}>
                                                <video
                                                    src={photoUrl}
                                                    className="inspection-photos-thumbnail-video"
                                                    muted
                                                    playsInline
                                                />
                                                <div className="inspection-photos-video-play-icon">
                                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                                                        <path d="M8 5v14l11-7z" />
                                                    </svg>
                                                </div>
                                            </div>
                                        ) : (
                                            <img
                                                src={photoUrl}
                                                alt={photo.label}
                                                onError={(e) => {
                                                    e.target.src = 'https://via.placeholder.com/150x100?text=No+Image';
                                                }}
                                                onClick={() => handlePhotoClick(photoWithChanges)}
                                                onMouseEnter={(e) => handlePhotoMouseEnter(photo, e)}
                                                onMouseLeave={handlePhotoMouseLeave}
                                            />
                                        )}

                                        {getPhotoStatus(photo) === REJECTED && (
                                            <div className="inspection-photos-rejection-overlay" onClick={() => handlePhotoClick(photoWithChanges)}>
                                                <span className="inspection-photos-rejection-label">RECHAZADO</span>
                                            </div>
                                        )}
                                    </div>

                                    <p className="inspection-photos-thumbnail-label">{photo.label}</p>

                                </div>
                            );
                        })}

                        {/* Preview de foto - renderizado una sola vez fuera del map */}
                        {previewPhoto && (
                            <PhotoPreview
                                isVisible={showPreview}
                                photo={{
                                    ...previewPhoto,
                                    url: photoUrls[previewPhoto.id] || previewPhoto.url || ''
                                }}
                                anchorPosition={anchorPosition}
                                onPreviewHoverChange={handlePreviewHoverChange}
                            />
                        )}

                        {/* Fill empty spaces to maintain 5-column layout */}
                        {row.length < 5 && Array.from({ length: 5 - row.length }).map((_, index) => (
                            <div key={`empty-${index}`} className="inspection-photos-photo-thumbnail empty"></div>
                        ))}
                    </div>
                ))}
            </div>
        );
    };

    const renderTabContent = () => {
        const sectionApprovalInfo = getSectionApprovalInfo();

        return (
            <div className="inspection-photos-tab-content-wrapper">
                {(() => {
                    switch (activeTab) {
                        case 'documents':
                            return renderPhotoGrid(categories.documents, 'Documentos Requeridos');
                        case 'exterior':
                            return renderPhotoGrid(categories.exterior, 'Fotos Exterior');
                        case 'interior':
                            return renderPhotoGrid(categories.interior, 'Fotos Interior');
                        case 'accessories':
                            return renderPhotoGrid(categories.accessories, 'Fotos de Accesorios');
                        default:
                            return renderPhotoGrid(categories.documents, 'Documentos Requeridos');
                    }
                })()}

                {/* Información de aprobación de la sección */}
                {sectionApprovalInfo && (
                    <div className="inspection-photos-section-approval-info">
                        <div className="section-approval-content">
                            <div className="section-approval-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 6L9 17l-5-5" />
                                </svg>
                            </div>
                            <div className="section-approval-text">
                                <h4 className="section-approval-title">Sección de Fotos Aprobada</h4>
                                <p className="section-approval-details">
                                    Aprobado por: <strong>{sectionApprovalInfo.approvedBy}</strong>
                                </p>
                                <p className="section-approval-details">
                                    Fecha de aprobación: <strong>{sectionApprovalInfo.approvedAt}</strong>
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const isEnabledApproveRejectPhotoButton = () => {
        if (!selectedPhoto) return false;
        const currentStatus = getPhotoStatus(selectedPhoto);

        const isPending = currentStatus === PENDING || currentStatus === 'pendiente';
        return ![APPROVED, REJECTED].includes(inspection.status) && isPending;
    };

    return (
        <div className="inspection-photos-component">
            <div className={`inspection-photos-content-section ${!enablePhotoSection() ? 'disabled' : ''}`}>
                <div className="inspection-photos-section-header">
                    <h2 className="inspection-photos-section-title">Fotos y Documentos de la Inspección</h2>

                    <div className="inspection-photos-download-section">
                        <button
                            className="btn btn-primary inspection-photos-download-zip-btn"
                            onClick={handleDownloadPhotosZip}
                            disabled={inspection.photos.length === 0}
                            title="Descargar todas las fotos en formato ZIP"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7,10 12,15 17,10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                            Descargar ZIP
                        </button>
                    </div>

                    <div className="inspection-photos-tab-buttons">
                        <button
                            className={`inspection-photos-tab-button ${activeTab === 'documents' ? 'active' : ''}`}
                            onClick={() => setActiveTab('documents')}
                        >
                            Documentos Requeridos
                            {getPhotoCount('documents') > 0 && (
                                <span className="inspection-photos-photo-count">({getPhotoCount('documents')})</span>
                            )}
                        </button>
                        <button
                            className={`inspection-photos-tab-button ${activeTab === 'exterior' ? 'active' : ''}`}
                            onClick={() => setActiveTab('exterior')}
                        >
                            Fotos Exterior
                            {getPhotoCount('exterior') > 0 && (
                                <span className="inspection-photos-photo-count">({getPhotoCount('exterior')})</span>
                            )}
                        </button>
                        <button
                            className={`inspection-photos-tab-button ${activeTab === 'interior' ? 'active' : ''}`}
                            onClick={() => setActiveTab('interior')}
                        >
                            Fotos Interior
                            {getPhotoCount('interior') > 0 && (
                                <span className="inspection-photos-photo-count">({getPhotoCount('interior')})</span>
                            )}
                        </button>
                        <button
                            className={`inspection-photos-tab-button ${activeTab === 'accessories' && getPhotoCount('accessories') > 0 ? 'active' : ''} ${getPhotoCount('accessories') === 0 ? 'disabled' : ''}`}
                            onClick={() => setActiveTab('accessories')}
                            disabled={getPhotoCount('accessories') === 0}
                        >
                            Fotos de Accesorios
                            {getPhotoCount('accessories') > 0 && (
                                <span className="inspection-photos-photo-count">({getPhotoCount('accessories')})</span>
                            )}
                        </button>
                    </div>
                </div>

                <div className="inspection-photos-tab-content">
                    {renderTabContent()}
                </div>
            </div>

            {/* 🔧 NUEVO: Mensaje de recomendación cuando se muestra el botón de guardar */}
            {showSaveButton && (
                <div className="inspection-photos-recommendation">
                    <div className="recommendation-content">
                        <div className="recommendation-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                            </svg>
                        </div>
                        <div className="recommendation-text">
                            <h4>Revisión de Fotos</h4>
                            <p>Revisa todas las fotos y rechaza solo aquellas que no cumplan con los criterios. Las fotos pendientes se aprobarán automáticamente al finalizar la inspección.</p>
                        </div>
                    </div>
                </div>
            )}

            <div className="inspection-photos-start-button-container">
                {enableRequestNewPhotos() && (
                    <button className="btn btn-primary" onClick={() => { }}>
                        Solicitar Nuevas Fotos
                    </button>
                )}

                {/* 🔧 NUEVO: Botón para guardar cambios */}
                {showSaveButton && (
                    <button
                        className={`btn btn-primary ${!isEnabledSaveButton() ? 'disabled' : ''}`}
                        onClick={() => handleSaveAllChanges()}
                        disabled={!isEnabledSaveButton()}
                    >
                        Guardar Cambios de Fotos
                    </button>
                )}
            </div>

            <PhotoModal
                selectedPhoto={selectedPhoto}
                closeModal={closeModal}
                photos={inspection.photos}
                open={openModal}
                enableApproveButtons={() => false}
                enableRejectButtons={isEnabledApproveRejectPhotoButton}
                enableUpdatePhotoButton={() => false}
                handleApprovePhoto={() => { }}
                handleRejectPhoto={handleShowRejectionModal}
                onDownloadPhoto={onDownloadPhoto}
                inspectionStatus={inspection.status}
            />

            <Modal
                isOpen={showRejectionModal}
                onClose={() => {
                    setShowRejectionModal(false);
                    setRejectionReason('');
                }}
                title="Motivo de Rechazo"
                size="small"
            >
                <div className="form-group">
                    <label htmlFor="rejectionReason">Se solicitará que se suba una nueva foto para continuar con la inspección.</label>
                    <textarea
                        id="rejectionReason"
                        placeholder="Ingrese el motivo de rechazo"
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                    />
                </div>
                <button className={`btn btn-cancel ${rejectionReason.length === 0 ? 'rejection-button-disabled' : ''}`} onClick={() => handleRejectSelectedPhoto(selectedPhoto)}>
                    Rechazar Foto
                </button>
            </Modal>

            {/* Modal de confirmación de rechazos */}
            <Modal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                title="Confirmar Rechazos de Fotos"
                size="medium"
            >
                <div className="confirm-rejections-content">
                    <p>¿Estás seguro de que quieres confirmar los siguientes rechazos?</p>

                    <div className="rejections-summary">
                        {getRejectedPhotos().map((rejection, index) => (
                            <div key={index} className="rejection-item">
                                <span className="rejection-photo-name">{rejection.label}</span>
                                <span className="rejection-reason">{rejection.rejectionReason}</span>
                            </div>
                        ))}
                    </div>

                    <p className="confirmation-warning">
                        <strong>⚠️ Importante:</strong> Una vez confirmado, se actualizará la información en la base de datos.
                    </p>
                </div>

                <div className="modal-actions">
                    <button
                        className="btn btn-cancel"
                        onClick={() => setShowConfirmModal(false)}
                    >
                        Cancelar
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={confirmAllRejections}
                    >
                        Confirmar Rechazos
                    </button>
                </div>
            </Modal>
        </div>
    );
};

export default InspectionPhotos;